import { useEffect, useState, useRef } from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Typography, Checkbox, TextField, FormControlLabel, Divider } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { httpClient, apiUrl } from '../../../../../config';
import { GOAL_NAMES, FB_EVENT_NAMES, EVENT_TYPES } from '../../../../../config/constants/apiConstants';
import { useIndustry } from '../../../../../context/IndustryContext';

// google conversion action name for the registration event (must match the action name in Google Ads)
const GOOGLE_SIGN_UP_NAME = 'Sign-up';

const REQUIRED_GOALS = new Set([GOAL_NAMES.PURCHASE, GOAL_NAMES.PURCHASE_CPA, GOAL_NAMES.PURCHASES]);

const DEFAULT_SELECTED_GOALS = new Set([GOAL_NAMES.PURCHASE, GOAL_NAMES.PURCHASE_CPA, GOAL_NAMES.PURCHASES]);

const DEFAULT_GOAL_MODIFICATIONS = {
  [GOAL_NAMES.PURCHASE]: FB_EVENT_NAMES.PURCHASE,
  [GOAL_NAMES.PURCHASE_CPA]: FB_EVENT_NAMES.PURCHASE_CPA,
  [GOAL_NAMES.PURCHASES]: FB_EVENT_NAMES.PURCHASE,
  [GOAL_NAMES.REGISTRATION]: GOOGLE_SIGN_UP_NAME,
};

const TRACKCB_EVENT_KEYS = [EVENT_TYPES.ADD_TO_CART, EVENT_TYPES.CHECK_OUT, EVENT_TYPES.PURCHASE, EVENT_TYPES.REGISTRATION];
const TRACKCB_DEFAULT_MAPPING = {
  [EVENT_TYPES.ADD_TO_CART]: FB_EVENT_NAMES.ADD_TO_CART,
  [EVENT_TYPES.CHECK_OUT]: FB_EVENT_NAMES.INITIATE_CHECKOUT,
  [EVENT_TYPES.PURCHASE]: FB_EVENT_NAMES.PURCHASE,
  [EVENT_TYPES.REGISTRATION]: GOOGLE_SIGN_UP_NAME,
};

const parseTrackCBMapping = (data) => {
  if (!data) return {};
  if (typeof data === 'object') return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return {};
};

export const GoogleConversionsGoalsTab = () => {
  const record = useRecordContext();
  const { setValue, setError, clearErrors } = useFormContext();
  const { industryId } = useIndustry();

  const [goalNames, setGoalNames] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState({});
  const [goalModifications, setGoalModifications] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const initializedRef = useRef(false);

  const [trackcbMapping, setTrackcbMapping] = useState(TRACKCB_DEFAULT_MAPPING);
  const trackcbInitializedRef = useRef(false);
  const trackcbSyncEnabled = useWatch({ name: 'settings.enable_pulling_data_trackcb' });

  const validateRequiredGoals = (modifications) => {
    const hasEmpty = [...REQUIRED_GOALS].some(goalName => !modifications[goalName]?.trim());
    if (hasEmpty) {
      setError('google_conversions_goal_mapping_tune', { type: 'manual', message: 'Conversion name is required for Purchase, Purchases and Purchase CPA goals' });
    } else {
      clearErrors('google_conversions_goal_mapping_tune');
    }
  };

  const validateTrackcbMapping = (mapping, syncEnabled) => {
    if (!syncEnabled) {
      clearErrors('google_conversions_goal_mapping_trackcb');
      return;
    }
    const hasEmpty = TRACKCB_EVENT_KEYS.some(key => !mapping[key]?.trim());
    if (hasEmpty) {
      setError('google_conversions_goal_mapping_trackcb', {
        type: 'manual',
        message: 'All TrackCB event mappings are required when TrackCB sync is enabled',
      });
    } else {
      clearErrors('google_conversions_goal_mapping_trackcb');
    }
  };

  useEffect(() => {
    if (initializedRef.current) return;

    const fetchGoals = async () => {
      try {
        const industryQuery = industryId ? `?industry_id=${industryId}` : '';
        const endpoint = record?.id
          ? `${apiUrl}/tables/media_platform_api_settings/goals/${record.id}`
          : `${apiUrl}/tables/conversion_reports/all_goals${industryQuery}`;

        const response = await httpClient(endpoint);
        if (!response?.json?.success) {
          throw new Error('Failed to fetch goals');
        }

        if (initializedRef.current) return;
        initializedRef.current = true;

        const { goals } = response.json;
        const names = goals.map(g => g.goal_name);
        const selected = {};
        const modifications = {};

        goals.forEach(({ goal_name, is_selected, modified_name }) => {
          if (record?.id) {
            selected[goal_name] = is_selected;
            modifications[goal_name] = modified_name || '';
          } else {
            selected[goal_name] = DEFAULT_SELECTED_GOALS.has(goal_name);
            modifications[goal_name] = DEFAULT_GOAL_MODIFICATIONS[goal_name] || '';
          }
        });

        setGoalNames(names);
        setSelectedGoals(selected);
        setGoalModifications(modifications);
        updateTuneFormValue({ setValue, names, selected, modifications });
        validateRequiredGoals(modifications);
      } catch (err) {
        setFetchError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [record?.id]);

  useEffect(() => {
    if (trackcbInitializedRef.current) return;
    trackcbInitializedRef.current = true;

    const existing = parseTrackCBMapping(record?.google_conversions_goal_mapping_trackcb);
    const initial = TRACKCB_EVENT_KEYS.reduce((acc, key) => {
      const existingValue = existing[key];
      acc[key] = existingValue && existingValue.trim() ? existingValue : TRACKCB_DEFAULT_MAPPING[key];
      return acc;
    }, {});

    setTrackcbMapping(initial);
    setValue('google_conversions_goal_mapping_trackcb', JSON.stringify(initial));
  }, [record?.id]);

  useEffect(() => {
    validateTrackcbMapping(trackcbMapping, trackcbSyncEnabled);
  }, [trackcbMapping, trackcbSyncEnabled]);

  const handleCheckboxChange = (goalName) => {
    if (REQUIRED_GOALS.has(goalName)) return;
    const updated = { ...selectedGoals, [goalName]: !selectedGoals[goalName] };
    setSelectedGoals(updated);
    updateTuneFormValue({ setValue, names: goalNames, selected: updated, modifications: goalModifications });
  };

  const handleTextChange = (goalName, val) => {
    const updated = { ...goalModifications, [goalName]: val };
    setGoalModifications(updated);
    updateTuneFormValue({ setValue, names: goalNames, selected: selectedGoals, modifications: updated });
    if (REQUIRED_GOALS.has(goalName)) {
      validateRequiredGoals(updated);
    }
  };

  const handleTrackcbChange = (key, val) => {
    const updated = { ...trackcbMapping, [key]: val };
    setTrackcbMapping(updated);
    setValue('google_conversions_goal_mapping_trackcb', JSON.stringify(updated));
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (fetchError) return <Typography color="error">{fetchError}</Typography>;

  return (
    <Box display="flex" flexDirection="row" alignItems="stretch" sx={{ gap: 4 }}>
      <Box sx={{ width: 680, flexShrink: 0 }}>
        <Typography variant="h5" fontWeight="bold">Tune Conversions Goals Mapping</Typography>
        <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={2} p={2}>
          <Typography variant="h6" noWrap>Select Goals</Typography>
          <Typography variant="h6" noWrap>Conversion Name (modified)</Typography>
        </Box>
        {goalNames.map((goalName) => {
          const isRequired = REQUIRED_GOALS.has(goalName);
          const hasError = isRequired && !goalModifications[goalName]?.trim();
          return (
            <Box key={goalName} display="grid" gridTemplateColumns="1fr 1fr" columnGap={2} alignItems="center" my={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedGoals[goalName]}
                    onChange={() => handleCheckboxChange(goalName)}
                    disabled={isRequired}
                  />
                }
                label={goalName}
              />
              <TextField
                label="Conversion Name"
                value={goalModifications[goalName] || ''}
                onChange={(e) => handleTextChange(goalName, e.target.value)}
                fullWidth
                InputLabelProps={{ required: isRequired }}
                error={hasError}
                helperText={hasError ? 'Required' : ''}
              />
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          width: '1px',
          alignSelf: 'stretch',
          backgroundColor: 'rgba(0, 0, 0, 0.23)',
        }}
      />

      <Box sx={{ width: 680, flexShrink: 0 }}>
        <Typography variant="h5" fontWeight="bold">TrackCB Conversions Goals Mapping</Typography>
        <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={2} p={2}>
          <Typography variant="h6" noWrap>Select Goals</Typography>
          <Typography variant="h6" noWrap>Conversion Name (modified)</Typography>
        </Box>
        {TRACKCB_EVENT_KEYS.map((key) => {
          const value = trackcbMapping[key] || '';
          const hasError = !!trackcbSyncEnabled && !value.trim();
          return (
            <Box key={key} display="grid" gridTemplateColumns="1fr 1fr" columnGap={2} alignItems="center" my={1}>
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label={key}
              />
              <TextField
                label="Conversion Name"
                value={value}
                onChange={(e) => handleTrackcbChange(key, e.target.value)}
                fullWidth
                InputLabelProps={{ required: !!trackcbSyncEnabled }}
                error={hasError}
                helperText={hasError ? 'Required when TrackCB sync is enabled' : ''}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const updateTuneFormValue = ({ setValue, names, selected, modifications }) => {
  const dataArray = names.map((goal) => ({
    goal_name: goal,
    is_selected: selected[goal],
    modified_name: modifications[goal] || '',
  }));

  setValue('google_conversions_goal_mapping_tune', JSON.stringify(dataArray));
};
